import makerjs from 'makerjs'
import Decimal from 'decimal.js'

// zxd.svg 中的形状元素（SVG 坐标，单位: point, 1mm = 2.83465pt）
const SCALE = 1 / 2.83465 // point -> mm

// 原始 SVG 元素的边界框中心（SVG 坐标）
const CX = 279.415
const CY = 219.415

// 将 SVG 坐标转为以中心为原点的 mm 坐标（Y轴翻转）
function toMM(svgX, svgY) {
    return [(svgX - CX) * SCALE, -(svgY - CY) * SCALE]
}

/**
 * 生成单个轴下垫模型（以原点为中心）
 * 形状来源: public/zxd.svg（最新真实路径）
 */
function generateSinglePad() {
    const pad = { paths: {}, models: {} }

    // ========== 1. 中心圆角矩形 — 四条直线边 ==========
    // 左竖边: M273.75,209.2 v-8.5
    pad.paths['rect_left'] = new makerjs.paths.Line(
        toMM(273.75, 209.2),
        toMM(273.75, 200.7)
    )
    // 上横边: M283.67,210.62 h-8.5
    pad.paths['rect_top'] = new makerjs.paths.Line(
        toMM(283.67, 210.62),
        toMM(275.17, 210.62)
    )
    // 右竖边: M285.09,200.7 v8.5
    pad.paths['rect_right'] = new makerjs.paths.Line(
        toMM(285.09, 200.7),
        toMM(285.09, 209.2)
    )
    // 下横边: M275.17,199.28 h8.5
    pad.paths['rect_bottom'] = new makerjs.paths.Line(
        toMM(275.17, 199.28),
        toMM(283.67, 199.28)
    )

    // ========== 2. 圆角矩形 — 四个圆角 ==========
    // 左下角: M275.17,210.62 c-.78,0 -1.42,-.63 -1.42,-1.42
    pad.models['corner_bl'] = new makerjs.models.BezierCurve(
        toMM(275.17, 210.62),
        toMM(275.17 - 0.78, 210.62),
        toMM(275.17 - 1.42, 210.62 - 0.63),
        toMM(275.17 - 1.42, 210.62 - 1.42)
    )
    // 右上角: M283.67,199.28 c.78,0 1.42,.63 1.42,1.42
    pad.models['corner_tr'] = new makerjs.models.BezierCurve(
        toMM(283.67, 199.28),
        toMM(283.67 + 0.78, 199.28),
        toMM(283.67 + 1.42, 199.28 + 0.63),
        toMM(283.67 + 1.42, 199.28 + 1.42)
    )
    // 左上角: M273.75,200.7 c0,-.78 .63,-1.42 1.42,-1.42
    pad.models['corner_tl'] = new makerjs.models.BezierCurve(
        toMM(273.75, 200.7),
        toMM(273.75, 200.7 - 0.78),
        toMM(273.75 + 0.63, 200.7 - 1.42),
        toMM(273.75 + 1.42, 200.7 - 1.42)
    )
    // 右下角: M285.09,209.2 c0,.78 -.63,1.42 -1.42,1.42
    pad.models['corner_br'] = new makerjs.models.BezierCurve(
        toMM(285.09, 209.2),
        toMM(285.09, 209.2 + 0.78),
        toMM(285.09 - 0.63, 209.2 + 1.42),
        toMM(285.09 - 1.42, 209.2 + 1.42)
    )

    // ========== 3. 三个圆 ==========
    // 小圆 (ellipse 实际是圆)
    {
        const [cx, cy] = toMM(265.1, 219.49)
        pad.paths['circle_small'] = new makerjs.paths.Circle([cx, cy], 2.83 * SCALE)
    }
    // 大圆
    {
        const [cx, cy] = toMM(279.41, 219.49)
        pad.paths['circle_large'] = new makerjs.paths.Circle([cx, cy], 5.67 * SCALE)
    }
    // 中圆
    {
        const [cx, cy] = toMM(272.21, 233.89)
        pad.paths['circle_medium'] = new makerjs.paths.Circle([cx, cy], 4.25 * SCALE)
    }

    // ========== 4. 右侧短弧 ==========
    // M291.46,217.79 c.8,-1.08 2.29,-1.46 3.53,-.84 s1.83,2.04 1.45,3.33
    {
        let curX = 291.46, curY = 217.79
        // 第一段 c
        const p0 = toMM(curX, curY)
        const c1 = toMM(curX + 0.8, curY - 1.08)
        const c2 = toMM(curX + 2.29, curY - 1.46)
        const p3 = toMM(curX + 3.53, curY - 0.84)
        pad.models['right_arc1'] = new makerjs.models.BezierCurve(p0, c1, c2, p3)

        // 第二段 s（平滑）
        curX = 291.46 + 3.53
        curY = 217.79 - 0.84
        // 前一段 c2 相对终点的反射
        const prevC2x = 291.46 + 2.29
        const prevC2y = 217.79 - 1.46
        const sc1x = curX + (curX - prevC2x)
        const sc1y = curY + (curY - prevC2y)
        const sc2 = toMM(curX + 1.83, curY + 2.04)
        const sp3 = toMM(curX + 1.45, curY + 3.33)
        pad.models['right_arc2'] = new makerjs.models.BezierCurve(
            toMM(curX, curY),
            toMM(sc1x, sc1y),
            sc2,
            sp3
        )
    }

    // ========== 5. 中圆附近弧 ==========
    // M294.27,227.75 c-.35,1.21 -1.23,2.25 -2.49,2.76 -2.18,.88 -4.66,-.17 -5.54,-2.34 -.58,-1.43 -.33,-2.99 .53,-4.15
    {
        let curX = 294.27, curY = 227.75
        // 段1
        const p0 = toMM(curX, curY)
        const c1 = toMM(curX - 0.35, curY + 1.21)
        const c2 = toMM(curX - 1.23, curY + 2.25)
        const p3 = toMM(curX - 2.49, curY + 2.76)
        pad.models['med_arc1'] = new makerjs.models.BezierCurve(p0, c1, c2, p3)

        // 段2
        curX = 294.27 - 2.49
        curY = 227.75 + 2.76
        const p0b = toMM(curX, curY)
        const c1b = toMM(curX - 2.18, curY + 0.88)
        const c2b = toMM(curX - 4.66, curY - 0.17)
        const p3b = toMM(curX - 5.54, curY - 2.34)
        pad.models['med_arc2'] = new makerjs.models.BezierCurve(p0b, c1b, c2b, p3b)

        // 段3
        curX = curX - 5.54
        curY = curY - 2.34
        const p0c = toMM(curX, curY)
        const c1c = toMM(curX - 0.58, curY - 1.43)
        const c2c = toMM(curX - 0.33, curY - 2.99)
        const p3c = toMM(curX + 0.53, curY - 4.15)
        pad.models['med_arc3'] = new makerjs.models.BezierCurve(p0c, c1c, c2c, p3c)
    }

    // ========== 6. 两条连接直线 ==========
    // line: (294.27,227.75) → (296.44,220.28)
    pad.paths['line1'] = new makerjs.paths.Line(
        toMM(294.27, 227.75),
        toMM(296.44, 220.28)
    )
    // line: (286.79,224.02) → (291.46,217.79)
    pad.paths['line2'] = new makerjs.paths.Line(
        toMM(286.79, 224.02),
        toMM(291.46, 217.79)
    )

    return pad
}

/**
 * 为所有按键生成轴下垫布局
 * @param {Array} keysArray - KLE 解析后的 Key 对象数组
 * @param {Object} options - { unitWidth, unitHeight, extraRotation, boardRadius }
 * @returns {Object} makerjs 模型
 */
export function buildPads(keysArray, options) {
    const {
        unitWidth = new Decimal(19.05),
        unitHeight = new Decimal(19.05),
        extraRotation = 0,
        boardRadius = new Decimal(2),
    } = options

    const canvas = { models: {} }
    const singlePad = generateSinglePad()

    let minX = Number.POSITIVE_INFINITY
    let minY = Number.POSITIVE_INFINITY
    let maxX = Number.NEGATIVE_INFINITY
    let maxY = Number.NEGATIVE_INFINITY

    let id = 0
    for (const key of keysArray) {
        const originX = key.centerX.times(unitWidth).toNumber()
        const originY = key.centerY.times(unitHeight).times(-1).toNumber()

        const padInstance = makerjs.model.clone(singlePad)

        const angle = key.angle.times(-1).toNumber() + extraRotation
        if (angle !== 0) {
            makerjs.model.rotate(padInstance, angle)
        }

        padInstance.origin = [originX, originY]
        canvas.models['Pad_' + id] = padInstance

        // 边界追踪
        const halfW = key.width.times(unitWidth).times(0.5).toNumber()
        const halfH = key.height.times(unitHeight).times(0.5).toNumber()
        const rad = angle * Math.PI / 180
        const cosA = Math.cos(rad)
        const sinA = Math.sin(rad)
        const corners = [
            [originX + (-halfW * cosA - (-halfH) * sinA), originY + (-halfW * sinA + (-halfH) * cosA)],
            [originX + (halfW * cosA - (-halfH) * sinA), originY + (halfW * sinA + (-halfH) * cosA)],
            [originX + (halfW * cosA - halfH * sinA), originY + (halfW * sinA + halfH * cosA)],
            [originX + (-halfW * cosA - halfH * sinA), originY + (-halfW * sinA + halfH * cosA)],
        ]
        for (const [cx, cy] of corners) {
            if (cx < minX) minX = cx
            if (cx > maxX) maxX = cx
            if (cy < minY) minY = cy
            if (cy > maxY) maxY = cy
        }

        id++
    }

    if (id > 0) {
        const boardRadiusNum = boardRadius ? boardRadius.toNumber() : 0
        const r = Math.min(boardRadiusNum, (maxX - minX) / 2, (maxY - minY) / 2)

        if (r > 0) {
            // 圆角边框
            canvas.models['BoundingBox'] = {
                paths: {
                    // 四条直线边
                    lineTop: new makerjs.paths.Line([minX + r, maxY], [maxX - r, maxY]),
                    lineBottom: new makerjs.paths.Line([minX + r, minY], [maxX - r, minY]),
                    lineLeft: new makerjs.paths.Line([minX, maxY - r], [minX, minY + r]),
                    lineRight: new makerjs.paths.Line([maxX, maxY - r], [maxX, minY + r]),
                    // 四个圆角
                    arcTopRight: new makerjs.paths.Arc([maxX - r, maxY - r], r, 0, 90),
                    arcBottomRight: new makerjs.paths.Arc([maxX - r, minY + r], r, 270, 360),
                    arcBottomLeft: new makerjs.paths.Arc([minX + r, minY + r], r, 180, 270),
                    arcTopLeft: new makerjs.paths.Arc([minX + r, maxY - r], r, 90, 180)
                }
            }
        } else {
            // 直角边框
            canvas.models['BoundingBox'] = {
                paths: {
                    lineTop: new makerjs.paths.Line([minX, maxY], [maxX, maxY]),
                    lineBottom: new makerjs.paths.Line([minX, minY], [maxX, minY]),
                    lineLeft: new makerjs.paths.Line([minX, maxY], [minX, minY]),
                    lineRight: new makerjs.paths.Line([maxX, maxY], [maxX, minY])
                }
            }
        }
    }

    return canvas
}